import {
  type Plan,
  type CoverageInfo,
  type OtherBenefit,
  type OtherBenefitCategory,
  UserLanguage,
  type PacePlan,
  type BasePlan,
  type ExtendedCoverageInfo,
  type OecStatus,
  PlanType,
  type PlanBenefitRedactionIssue,
  RedactionType,
  type DrugPricingRedactionIssue,
  type Redaction,
  type SearchResultPlan,
  CoverageType,
  type AdditionalSupplementalBenefits,
  OtherRedactionIssue,
  type RolloverCoveragePlan,
  type PlanIdParts,
  type RolloverCoverage,
} from "../@types";
import { getPlanInfoForNextYear } from "../api";
import { isMaPlanType } from "./planTypes";
import {
  DrugPricingRedactionIssues,
  PlanBenefitRedactionIssues,
} from "./constants";
import { type ApiError, logError } from "./errors";
import {
  parsePlanLongIdIntoUrlPartForApi,
  makePlanLongId,
} from "./objectUtilities";
import {
  getSupplementalBenefitCategoryName,
  getSupplementalBenefitName,
} from "./benefitNameHelpers";
import { useAppState } from "./context-hooks/useAppState";
import { useSearchParams } from "react-router-dom";

/**
 * Re-exported from @types/guards.ts (not defined here) to avoid a circular
 * dependency: guards.ts -> planHelpers.ts -> api.ts -> guards.ts.
 */
export { isValidPlanLongId } from "../@types/guards";

export const shouldSuppressPlanBenefits = (redactions: Redaction[]): boolean =>
  redactions.some(
    redaction =>
      redaction.type === RedactionType.suppression &&
      PlanBenefitRedactionIssues.includes(
        redaction.issue as PlanBenefitRedactionIssue
      )
  );

/**
 * Used for calculating the portion of an MSA plan's deductible for which a bene
 * is responsible.
 *
 * As of OE 2024, the `annual_deductible` property for an MSA plan is a string
 * that has text like "$9,400 annual deductible"
 *
 * Other plans may have very different strings, even containing both in-network
 * and out-of-network deductible values, but MSAs have no concept of OON & IN
 *
 * The assumption about what this string will be might be subject to breakage,
 * but for now, works
 */
export const calculateYourMSAResponsibility = (
  healthDeductible: number | string,
  msaYearlyDeposit: number
): number => {
  let deductible = healthDeductible;

  if (typeof deductible === "string") {
    deductible = deductible.replaceAll(/\D/g, "");
  }

  const result = Number(deductible) - msaYearlyDeposit;

  return Math.max(result, 0);
};

export const shouldSuppressDrugPricing = (redactions: Redaction[]): boolean =>
  redactions.some(
    redaction =>
      redaction.type === RedactionType.suppression &&
      DrugPricingRedactionIssues.includes(
        redaction.issue as DrugPricingRedactionIssue
      )
  );

export const fetchPlanIdsForNextYear = async (
  coverage: CoverageInfo
): Promise<RolloverCoveragePlan[] | null> => {
  const parsedCoverage = {
    ...coverage,
    contract_year: coverage.coverage_year,
  };

  if (
    !parsedCoverage.contract_id ||
    !parsedCoverage.contract_year ||
    !parsedCoverage.plan_id ||
    !parsedCoverage.segment_id
  ) {
    return null;
  }

  try {
    const nextYearPlanIds = await getPlanInfoForNextYear(
      parsePlanLongIdIntoUrlPartForApi(makePlanLongId(parsedCoverage))
    );
    return nextYearPlanIds;
  } catch (e) {
    logError("Could not get plan info for next year", e as ApiError);
    return null;
  }
};

export const getAdditionalBenefit = (
  plan: Plan,
  type: keyof AdditionalSupplementalBenefits,
  category: string,
  benefit: string
): OtherBenefit | null => {
  const cat = getSupplementalBenefitCategoryName(category);
  const bene = getSupplementalBenefitName(benefit);

  if (plan.additional_supplemental_benefits) {
    const otherCategory = plan.additional_supplemental_benefits[type].find(
      (benefit: OtherBenefitCategory) => benefit.category === cat
    );

    if (otherCategory) {
      const otherBenefit = otherCategory.benefits.find(
        (benefit: OtherBenefit) => benefit.benefit === bene
      );

      if (otherBenefit) {
        return otherBenefit;
      }
    }
  }

  return null;
};

export const planHasDrugCoverage = (plan: Plan | SearchResultPlan): boolean =>
  !isMaPlanType(plan.plan_type);

export const getPlanName = (
  plan:
    BasePlan | PacePlan | ExtendedCoverageInfo | OecStatus | RolloverCoverage,
  language: UserLanguage
): string => {
  let planNameToReturn = "name" in plan ? plan.name : plan.plan_name;

  if ("name" in plan) {
    planNameToReturn =
      language === UserLanguage.English
        ? plan.name
        : plan.name_esp || plan.name;
  } else {
    planNameToReturn =
      language === UserLanguage.English
        ? plan.plan_name
        : plan.plan_name_esp || plan.plan_name;
  }

  return planNameToReturn || "";
};

export const getPlanTypeForCoverageType = (
  coverageType: CoverageType
): string =>
  coverageType === CoverageType.MAPD ? PlanType.MAPD : PlanType.PDP;

/** @example "2021/H5652/002/0" */
export const isValidApiLongPlanId = (id?: string): boolean =>
  id ? /\d{4}\/[A-Z|0-9]+\/\d+\/\d+/.test(id) : false;

/**
 * Returns the plan year according to whichever property contains it, or an empty string
 */
export const getPlanYearToUse = (
  plan: OecStatus | SearchResultPlan | ExtendedCoverageInfo
) => {
  let yearToUse = "";
  if ("year" in plan) {
    yearToUse = plan.year;
  } else if ("coverage_year" in plan) {
    yearToUse = plan.coverage_year;
  } else if ("contract_year" in plan && plan.contract_year) {
    yearToUse = plan.contract_year;
  }
  return yearToUse;
};

/**
 * Asserts whether a plan's contract year is GT or LT a given test year
 * @param params
 * @param params.contractYear - A `plan.contract_year` string
 * @param params.testYear - A numeric year value
 * @param [params.test] - Can be set to `"greaterThan"` or `"lessThan"`
 */
export const contractYearTest = ({
  contractYear,
  testYear,
  test,
}: {
  contractYear: string;
  testYear: number;
  test: "greaterThan" | "lessThan";
}): boolean => {
  const contractYearNum = Number(contractYear);
  if (isNaN(contractYearNum)) return false;
  return test === "greaterThan"
    ? contractYearNum > testYear
    : contractYearNum < testYear;
};

export const contractYearLessThan = (contractYear: string, testYear: number) =>
  contractYearTest({ contractYear, testYear, test: "lessThan" });

export const contractYearGreaterThan = (
  contractYear: string,
  testYear: number
) => contractYearTest({ contractYear, testYear, test: "greaterThan" });

export const isCoverageSameAsPlan = (
  coverageInfo: CoverageInfo | null | undefined,
  plan: PlanIdParts
): boolean => {
  if (!coverageInfo) return false;

  return (
    coverageInfo.plan_id === plan.plan_id &&
    coverageInfo.segment_id === plan.segment_id &&
    coverageInfo.contract_id === plan.contract_id &&
    (coverageInfo.contract_year === plan.contract_year ||
      coverageInfo.coverage_year === plan.contract_year)
  );
};

export const getCantEnrollPlan = ({
  coverage,
  isCsr,
  plan: { plan_id, contract_id, contract_year, segment_id },
}: {
  coverage: SearchResultPlan | null | undefined;
  isCsr: boolean;
  plan: Plan | SearchResultPlan;
}) =>
  !isCsr &&
  isCoverageSameAsPlan(coverage, {
    plan_id,
    contract_id,
    contract_year,
    segment_id,
  });

export const getPlanHasDsnpSuppression = ({
  redactions,
}: Plan | SearchResultPlan) =>
  redactions.some(
    r =>
      r.type === RedactionType.suppression &&
      r.issue === OtherRedactionIssue.d_snp
  );

export const getPlanIsFrozen = ({ redactions }: Plan | SearchResultPlan) =>
  redactions.some(
    r =>
      r.type === RedactionType.frozen &&
      r.issue === OtherRedactionIssue.frzn_enrlmnt
  );

export const usePlanType = (): PlanType | undefined => {
  const { planType } = useAppState();
  const [searchParams] = useSearchParams();
  const urlPlanType = searchParams.get("plan_type");
  return (
    planType ?? (urlPlanType as ReturnType<typeof useAppState>["planType"])
  );
};
